import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Avatar, Tooltip, IconButton, Divider,
  Menu, MenuItem, ListItemIcon,
} from '@mui/material';
import {
  LogoutOutlined, AdminPanelSettingsOutlined, AccountCircleOutlined,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { alpha } from '@mui/material/styles';

const PRIMARY = '#1075BA';

export default function DashboardLayout() {
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const selectedOrg = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('gv_selected_org') || 'null'); } catch { return null; }
  }, []);

  const exitSelectedOrg = () => {
    localStorage.removeItem('gv_selected_org');
    navigate('/superadmin');
  };

  const orgName    = selectedOrg?.name || user?.organization?.name  || 'GoVirtual';
  const orgLogo    = user?.organization?.logo  || null;
  const orgColor   = user?.organization?.primaryColor || PRIMARY;
  const hue        = ((user?.firstName?.charCodeAt(0) || 65) * 13) % 360;
  const initials   = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`;
  const orgInitials = orgName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f0f4f8' }}>

      {/* ── Top header ──────────────────────────────────────────────────────── */}
      <Box sx={{
        height: 56,
        background: '#fff',
        borderBottom: '1px solid #e8edf2',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        display: 'flex', alignItems: 'center',
        px: { xs: 2, md: 3 },
        gap: 2,
        position: 'sticky', top: 0, zIndex: 100,
        flexShrink: 0,
      }}>
        {/* Left — org name / logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
          {orgLogo ? (
            <Box sx={{
              width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
              overflow: 'hidden', border: '1px solid #e4e9f0',
              background: '#f8f9fb',
            }}>
              <Box component="img" src={orgLogo} alt={orgName}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </Box>
          ) : (
            <Box sx={{
              width: 32, height: 32, borderRadius: 1.5,
              background: orgColor,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Typography sx={{ color: '#fff', fontSize: '0.72rem', fontWeight: 800, lineHeight: 1 }}>
                {orgInitials}
              </Typography>
            </Box>
          )}
          <Typography sx={{
            fontWeight: 700, fontSize: '0.95rem', color: '#1a1a2e',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {orgName}
          </Typography>
        </Box>

        {/* Right — superadmin + account menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {/* Exit company view banner */}
          {isSuperAdmin && selectedOrg && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 600 }}>
                Viewing as {selectedOrg.name}
              </Typography>
              <Button size="small" onClick={exitSelectedOrg}
                sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.75rem', height: 28, px: 1.25, color: '#7c3aed', border: '1px solid', borderColor: alpha('#7c3aed', 0.3), '&:hover': { background: alpha('#7c3aed', 0.06) } }}>
                Exit
              </Button>
            </Box>
          )}
          {isSuperAdmin && (
            <Tooltip title="Superadmin Console">
              <Button
                size="small"
                startIcon={<AdminPanelSettingsOutlined sx={{ fontSize: 16 }} />}
                onClick={() => navigate('/superadmin')}
                sx={{
                  borderRadius: 2, textTransform: 'none', fontSize: '0.8rem',
                  color: '#7c3aed', border: '1px solid',
                  borderColor: alpha('#7c3aed', 0.3),
                  background: alpha('#7c3aed', 0.06),
                  '&:hover': { background: alpha('#7c3aed', 0.1), borderColor: '#7c3aed' },
                  px: 1.5, height: 32,
                }}
              >
                Superadmin
              </Button>
            </Tooltip>
          )}

          {/* Account avatar — opens menu */}
          <Tooltip title={`${user?.firstName} ${user?.lastName}`}>
            <Avatar
              onClick={e => setAnchorEl(e.currentTarget)}
              sx={{
                width: 32, height: 32, cursor: 'pointer',
                bgcolor: `hsl(${hue},50%,88%)`,
                color: `hsl(${hue},40%,30%)`,
                fontSize: '0.72rem', fontWeight: 800,
                border: `1.5px solid ${alpha(PRIMARY, 0.2)}`,
              }}
            >
              {initials}
            </Avatar>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              elevation: 2,
              sx: { mt: 0.75, borderRadius: 2.5, minWidth: 200, border: '1px solid #e8edf2', overflow: 'visible' },
            }}
          >
            {/* User info */}
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a2e' }}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
            </Box>
            <Divider sx={{ borderColor: '#f0f2f5' }} />

            <MenuItem onClick={() => { setAnchorEl(null); }} sx={{ gap: 1.5, py: 1, fontSize: '0.875rem' }}>
              <ListItemIcon><AccountCircleOutlined sx={{ fontSize: 18 }} /></ListItemIcon>
              Account
            </MenuItem>

            <Divider sx={{ borderColor: '#f0f2f5' }} />

            <MenuItem
              onClick={() => { setAnchorEl(null); handleLogout(); }}
              sx={{ gap: 1.5, py: 1, fontSize: '0.875rem', color: '#d32f2f' }}
            >
              <ListItemIcon><LogoutOutlined sx={{ fontSize: 18, color: '#d32f2f' }} /></ListItemIcon>
              Sign Out
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* ── Page content ────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 2, md: 3 } }}>
        <Outlet />
      </Box>
    </Box>
  );
}